/**
 * RecordingPanel
 *
 * Compact container for call recording functionality.
 * When running inside a Halo PSA iframe, opens a popup window for recording
 * (since iframes block microphone access without allow="microphone").
 * When running standalone, uses the in-page modal flow.
 */

import { useState, useEffect, useCallback } from 'react'
import { Mic, Phone, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCallRecordingContext } from '@/contexts/CallRecordingContext'
import { CallRecorderModal } from '@/components/calls/CallRecorderModal'
import { FloatingRecorderWidget } from '@/components/calls/FloatingRecorderWidget'
import { CallNotesModal } from '@/components/calls/CallNotesModal'

interface RecordingPanelProps {
  ticketId: number | null
  ticketSummary: string | null
  isDark: boolean
}

function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

export function RecordingPanel({ ticketId, ticketSummary, isDark }: RecordingPanelProps) {
  const [showRecorderModal, setShowRecorderModal] = useState(false)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [popupMessage, setPopupMessage] = useState<string | null>(null)

  const { status, isRecording, setRecordingId, setStatus } = useCallRecordingContext()

  const numericTicketId = ticketId ?? undefined
  const inIframe = isInIframe()

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'halopod-recording-complete') {
        setPopupMessage(`Recording complete${event.data.recordingId ? ` - ID: ${event.data.recordingId}` : ''}`)
        if (event.data.recordingId) {
          setRecordingId(event.data.recordingId)
          setStatus('complete')
        }
      } else if (event.data?.type === 'halopod-recording-cancelled') {
        setPopupMessage(null)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [setRecordingId, setStatus])

  const openRecorderPopup = useCallback(() => {
    const params = new URLSearchParams({ mode: 'recorder' })
    if (ticketId) params.set('ticketId', String(ticketId))
    if (ticketSummary) params.set('ticketSummary', ticketSummary)
    params.set('theme', isDark ? 'dark' : 'light')

    const width = 380
    const height = 520
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2

    const popup = window.open(
      `${window.location.origin}/?${params.toString()}`,
      'halopod-recorder',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    )

    if (!popup) {
      setPopupMessage('Popup blocked. Please allow popups for this site and try again.')
    }
  }, [ticketId, ticketSummary, isDark])

  const handleRecordClick = () => {
    if (inIframe) {
      openRecorderPopup()
    } else {
      setShowRecorderModal(true)
    }
  }

  const handleProcessingComplete = () => {
    setShowNotesModal(true)
  }

  const isActive = isRecording || status === 'recording' || status === 'uploading' || status === 'processing'

  return (
    <div className="flex flex-col h-full">
      {isActive ? (
        <div className={`flex items-center gap-3 p-3 m-3 rounded-lg ${isDark ? 'bg-red-900/30 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
          <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse shrink-0" />
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-medium ${isDark ? 'text-red-300' : 'text-red-800'}`}>Recording in progress</p>
            {ticketId && <p className={`text-[10px] truncate ${isDark ? 'text-red-400' : 'text-red-600'}`}>Ticket #{ticketId}</p>}
          </div>
          <Phone className="h-4 w-4 text-red-500 shrink-0" />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center flex-1 gap-3 p-4">
          <div className={`flex items-center justify-center w-12 h-12 rounded-full ${isDark ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
            <Phone className={`h-6 w-6 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
          </div>
          <div className="text-center">
            <h3 className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>Call Recording - Beta Testing</h3>
            <p className={`text-[10px] mt-0.5 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
              Record, transcribe, and add notes to tickets
            </p>
          </div>
          {status !== 'complete' && (
            <Button size="sm" onClick={handleRecordClick} style={{ backgroundColor: '#2563eb', color: 'white' }} className="gap-1.5">
              {inIframe ? <ExternalLink className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              Record Call
            </Button>
          )}
          {inIframe && status !== 'complete' && (
            <p className={`text-[9px] text-center max-w-[180px] ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>
              Opens in a new window (mic requires standalone page)
            </p>
          )}
          {status === 'complete' && !showNotesModal && (
            <Button variant="outline" size="sm" onClick={() => setShowNotesModal(true)} className="gap-1.5">
              <Mic className="h-3.5 w-3.5" />View Notes
            </Button>
          )}
          {popupMessage && <p className="text-[10px] text-green-600 text-center">{popupMessage}</p>}
        </div>
      )}

      {!inIframe && (
        <>
          <CallRecorderModal
            isOpen={showRecorderModal}
            onClose={() => setShowRecorderModal(false)}
            ticketId={numericTicketId}
            ticketSummary={ticketSummary ?? undefined}
            onRecordingStart={() => {}}
          />
          <FloatingRecorderWidget onProcessingComplete={handleProcessingComplete} />
        </>
      )}

      <CallNotesModal isOpen={showNotesModal} onClose={() => setShowNotesModal(false)} />
    </div>
  )
}
